'use client'

import { ArrowRight, Shield } from 'lucide-react'

import { resolveClientCopy } from '@/lib/client-copy'
import { useSiteSettings } from '@/lib/site-settings-context'
import { GoogleLogo } from '@/components/google-logo'

/**
 * Public share link to the Rock'n Earthworks Google Business listing. Lands
 * on the reviews tab. Distinct from `googleReviewsUrl` in env, which points
 * at the leave-a-review form used by the post-job review gate.
 */
const GOOGLE_LISTING_URL = 'https://share.google/VMPdnCOBJwmvMc2mv'

export function TrustStrip() {
  const { abn, liabilityCover, testimonial, copy } = useSiteSettings()
  const { siteLabels } = resolveClientCopy(copy)

  return (
    <div className="mt-6 pt-6">
      {/* Shimmer divider matching the brand glow */}
      <div className="divider-glow mb-6 h-px" />

      {/* Badges row: Google CTA + ABN/insurance. Two equal-weight pills so the
          legitimacy signals read as a pair. Stacks on mobile. */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <a
          href={GOOGLE_LISTING_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="group inline-flex items-center gap-2 rounded-full border border-[#dadce0] bg-white px-4 py-2 text-sm text-[#3a3a3a] shadow-sm transition-colors hover:border-[#5f6368] hover:text-[#1a1a1a]"
        >
          <GoogleLogo size={16} />
          <span className="font-heading tracking-wide">{siteLabels.googleReviewsLabel}</span>
          <ArrowRight
            className="size-3 text-[#5f6368] transition-transform group-hover:translate-x-0.5"
            aria-hidden="true"
          />
        </a>

        <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-2">
          <Shield className="text-brand-green size-4 shrink-0" aria-hidden="true" />
          <span className="font-heading text-sm tracking-wider text-[#bbb] uppercase">
            {siteLabels.trustStripAbnLabel} {abn} · {liabilityCover}{' '}
            {siteLabels.trustStripInsuredLabel}
          </span>
        </div>
      </div>

      {/* Featured testimonial as a sticky-note pull-quote. Marker font does
          the punch work on the opening glyph and signature; the body sits in
          the sans face so a real review can actually be read. Slight tilt
          keeps the punk energy. */}
      <figure
        className="relative mt-6 rounded-sm border border-white/5 bg-[#1a0a1a]/60 p-5 sm:p-6"
        style={{ transform: 'rotate(-0.4deg)' }}
      >
        <span
          aria-hidden="true"
          className="font-marker text-brand-purple-light absolute -top-3 left-3 text-5xl leading-none drop-shadow-[0_0_8px_rgba(233,149,232,0.4)] sm:-top-4 sm:text-6xl"
        >
          &ldquo;
        </span>
        <blockquote className="pt-2 font-sans text-base leading-relaxed text-[#d8d8d8] sm:text-[15px]">
          {testimonial.quote}
        </blockquote>
        <figcaption className="font-marker text-brand-purple-light mt-3 text-lg tracking-wide">
          {testimonial.author}
          {testimonial.location && `, ${testimonial.location}`}
        </figcaption>
      </figure>
    </div>
  )
}
